import styles from './singleOrderView.module.scss';
import {useParams} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {GET_STOCKS_ORDER_BY_ID} from 'graphql/queries/stocks';
import {toast} from 'react-toastify';
import {Spin, Table, Tag, Card, Row, Col, Typography} from 'antd';
import moment from 'moment';
import backIcon from 'images/back.png';
import {useNavigate} from 'react-router-dom';
import {DerivedMetrics} from './derivedMetrics';
import {formatDecimal, formatDecimalWithCurrency} from './formatDecimal';
import {latestHoldReason} from '../ViewOrders/holdReason';

const {Title, Text} = Typography;

export const SingleOrderView = () => {
  const {id} = useParams();
  const navigate = useNavigate();

  const {data, loading} = useQuery(GET_STOCKS_ORDER_BY_ID, {
    variables: {input: {id, limit: 1}},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h2>Loading Order...</h2>
      </div>
    );
  }

  if (!data?.getOrders?.data) {
    return (
      <div className={styles.singleOrderView}>
        <div className={styles.singleOrderView__header}>
          <img onClick={() => navigate(-1)} src={backIcon} alt="Back" />
          <Title level={2}>Order Not Found</Title>
        </div>
      </div>
    );
  }

  const orderData = data.getOrders.data[0];

  // Helper function to get display value
  const getDisplayValue = (value, fallback = 'N/A') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    if (typeof value === 'object') {
      return value.name || value.ticker || value.id || fallback;
    }
    return value;
  };

  // Helper function to get price value
  const getPriceValue = () => {
    if (orderData.instrument?.currentMarketPrice) {
      return formatDecimal(orderData.instrument.currentMarketPrice);
    }
    if (orderData.instrument?.currentNetAssetValue) {
      return formatDecimal(orderData.instrument.currentNetAssetValue);
    }
    if (orderData.company?.stock?.lastPrice) {
      return formatDecimal(orderData.company.stock.lastPrice);
    }
    return 'N/A';
  };

  const getCumulativeFillValue = input => {
    const direction = input?.direction;
    let amount = 'N/A';
    if (direction === 'BUY' && input?.fillValue) {
      amount = input.fillValue - input.orderFees;
    } else if (direction === 'SELL' && input?.fillValue) {
      amount = input.fillValue;
    }
    return amount;
  };

  // History table columns
  const historyColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => (
        <Tag
          color={
            {
              'FILLED': 'green',
              'PENDING': 'orange',
              'ERROR': 'red',
              'REJECTED': 'red',
              'CANCELLED': 'red',
              'SETTLED': 'cyan',
              'PARTIALLY_FILLED': 'yellow',
            }[record?.status] || 'blue'
          }
          key={record.id}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Match Order',
      dataIndex: 'matchOrder',
      key: 'matchOrder',
      render: (text, record) =>
        record?.matchOrder && (
          <span
            style={{
              color: '#1890ff',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={() =>
              navigate(`/dashboard/stocks/view-order/${record.matchOrder}`)
            }>
            View Order
          </span>
        ),
    },
    {
      title: 'Price Filled',
      dataIndex: 'input',
      key: 'priceFilled',
      render: (text, record) => (
        <span>
          {record?.input?.currency && `${record.input.currency}`}
          {formatDecimal(record?.input?.assetPrice)}
        </span>
      ),
    },
    {
      title: 'Original Quantity',
      dataIndex: 'quantity',
      key: 'originalQuantity',
      render: () => (
        <span>
          {formatDecimal(
            orderData?.origination?.quantity ?? orderData?.quantity,
          )}
        </span>
      ),
    },

    {
      title: 'Executed Quantity',
      dataIndex: 'input',
      key: 'quantityFilled',
      render: (text, record) => (
        <span>{formatDecimal(record?.input?.fillQuantity)}</span>
      ),
    },
    {
      title: 'Cumulative Fill Value',
      dataIndex: 'input',
      key: 'fillValue',
      render: (text, record) => (
        <span>{formatDecimal(getCumulativeFillValue(record?.input))}</span>
      ),
    },
    {
      title: 'Fees',
      dataIndex: 'input',
      key: 'fees',
      render: (text, record) => (
        <span>{formatDecimal(record?.input?.orderFees)}</span>
      ),
    },
    {
      title: 'Broker',
      dataIndex: 'broker',
      key: 'broker',
      render: (text, record) => {
        // Extract broker from input.proportion array
        // Format: "00006863__3.00 units__XOF2400.00__INVICTUS"
        // Broker is after the 3rd underscore (index 3 when split by "__")
        if (
          record?.input?.proportion &&
          Array.isArray(record.input.proportion)
        ) {
          const brokerNames = record?.input?.proportion
            ?.map(proportion => {
              if (typeof proportion === 'string' && proportion.includes('__')) {
                const parts = proportion?.split('__');
                return parts?.length > 3 ? parts[3] : null;
              }
              return null;
            })
            .filter(Boolean);

          if (brokerNames?.length > 0) {
            return <span>{brokerNames.join(', ')}</span>;
          }
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Broker Reference',
      dataIndex: 'brokerOrderId',
      key: 'brokerOrderId',
      render: (text, record) => (
        <span>{record?.input?.brokerOrderId || 'N/A'}</span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text, record) => (
        <span>{moment(record?.createdAt).format('DD MMM, YYYY h:mmA')}</span>
      ),
    },
  ];

  // Notes table columns
  const notesColumns = [
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: '40%',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: createdBy =>
        createdBy
          ? `${createdBy?.firstName} ${createdBy?.lastName} (${createdBy?.email})`
          : 'N/A',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: date => moment(date).format('DD MMM, YYYY. h:mmA'),
    },
  ];

  // Fee schedule table columns
  const feeScheduleColumns = [
    {
      title: 'Fee Name',
      dataIndex: 'feeName',
      key: 'feeName',
    },
    {
      title: 'Fee Type',
      dataIndex: 'feeType',
      key: 'feeType',
    },
    {
      title: 'Fixed Amount',
      dataIndex: 'fixedFeeAmount',
      key: 'fixedFeeAmount',
      render: amount =>
        amount
          ? formatDecimalWithCurrency(amount, orderData.assetCurrency)
          : 'N/A',
    },
    {
      title: 'Variable Amount',
      dataIndex: 'variableFeeAmount',
      key: 'variableFeeAmount',
      render: amount => (amount ? `${formatDecimal(amount)}%` : 'N/A'),
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
    },
    {
      title: 'Recursion Day',
      dataIndex: 'origination',
      key: 'origination',
    },
    {
      title: 'Scalable',
      dataIndex: 'scalable',
      key: 'scalable',
      render: scalable => (
        <Tag color={scalable ? 'green' : 'red'}>{scalable ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
    },
  ];

  return (
    <div className={styles.singleOrderView}>
      <div className={styles.singleOrderView__header}>
        <img onClick={() => navigate(-1)} src={backIcon} alt="Back" />
        <Title level={2}>Order Details</Title>
      </div>

      {/* Basic Order Information */}
      <Card title="Basic Order Information" className={styles.card}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Order ID:</Text>
              <Text>{id}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Ticker:</Text>
              <Text>{getDisplayValue(orderData.symbol)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Instrument:</Text>
              <Text>
                {getDisplayValue(orderData.instrumentKey)?.replace(/_/g, ' ')}
              </Text>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Currency:</Text>
              <Text>{getDisplayValue(orderData.assetCurrency)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Direction:</Text>
              <Tag color={orderData.direction === 'BUY' ? 'green' : 'red'}>
                {getDisplayValue(orderData.direction)}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Order Type:</Text>
              <Text>{getDisplayValue(orderData.type)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Status:</Text>
              <Tag
                color={
                  {
                    'FILLED': 'green',
                    'PENDING': 'orange',
                    'ERROR': 'red',
                    'REJECTED': 'red',
                    'CANCELLED': 'red',
                    'SETTLED': 'cyan',
                    'EXPIRED': 'red',
                    'PARTIALLY_FILLED': 'yellow',
                  }[orderData?.status] || 'blue'
                }>
                {getDisplayValue(orderData?.status)?.replace(/_/g, ' ')}
              </Tag>
            </div>
          </Col>
          {orderData?.hold && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className={styles.field}>
                <Text strong>Hold:</Text>
                <Tag color="red">ON HOLD</Tag>
                <Text>
                  {latestHoldReason(orderData?.notes) || 'No reason recorded'}
                </Text>
              </div>
            </Col>
          )}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>TotalQuantity:</Text>
              <Text>{formatDecimal(orderData?.quantity)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Filled Quantity:</Text>
              <Text>{formatDecimal(orderData?.fillQuantity)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong> Fill Price:</Text>
              <Text>{formatDecimal(orderData?.avgFillPrice)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Fill Value:</Text>
              <Text>
                {formatDecimal(
                  orderData?.direction === 'BUY' &&
                    ['FILLED', 'SETTLED'].includes(orderData?.status)
                    ? orderData?.fillValue - orderData?.feeCharged
                    : orderData?.fillValue,
                )}
              </Text>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Current Asset Price:</Text>
              <Text>{getPriceValue()}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Last Fill Date:</Text>
              <Text>
                {orderData.lastFillDate
                  ? moment(orderData.lastFillDate).format('DD MMM, YYYY. h:mmA')
                  : 'N/A'}
              </Text>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Asset Class:</Text>
              <Text>{getDisplayValue(orderData.assetClass)}</Text>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Fractionable:</Text>
              <Tag color={orderData.fractionable ? 'green' : 'red'}>
                {orderData.fractionable ? 'Yes' : 'No'}
              </Tag>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Payment Method:</Text>
              <Text>{getDisplayValue(orderData.paymentMethod)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Collection:</Text>
              <Text>{getDisplayValue(orderData.collection)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Expiration Date:</Text>
              <Text>
                {orderData?.expiresAt
                  ? moment(orderData?.expiresAt).format('DD MMM, YYYY. h:mmA')
                  : 'N/A'}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Assigned Broker */}
      <Card title="Assigned Broker" className={styles.card}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Broker:</Text>
              <Text>{getDisplayValue(orderData.broker?.name)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Email:</Text>
              <Text>{getDisplayValue(orderData.broker?.email)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Commission:</Text>
              <Text>{formatDecimal(orderData.broker?.commission)}</Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Asset Class:</Text>
              <Text>
                {orderData.broker?.assetClass
                  ? orderData.broker.assetClass.replace(/_/g, ' ')
                  : 'N/A'}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Executed Brokers */}
      {orderData.brokers && orderData.brokers.length > 0 && (
        <Card title="Executed Brokers" className={styles.card}>
          <Table
            dataSource={orderData.brokers.map((broker, index) => ({
              ...broker,
              key: broker.id || index,
            }))}
            columns={[
              {
                title: 'Broker Name',
                dataIndex: 'name',
                key: 'name',
                render: name => <Text>{getDisplayValue(name)}</Text>,
              },
              {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                render: email => <Text>{getDisplayValue(email)}</Text>,
              },
              {
                title: 'Commission',
                dataIndex: 'commission',
                key: 'commission',
                render: commission => <Text>{formatDecimal(commission)}</Text>,
              },
              {
                title: 'Asset Class',
                dataIndex: 'assetClass',
                key: 'assetClass',
                render: assetClass => (
                  <Text>
                    {assetClass ? assetClass.replace(/_/g, ' ') : 'N/A'}
                  </Text>
                ),
              },
            ]}
            pagination={false}
            scroll={{x: true}}
            size="small"
          />
        </Card>
      )}

      {/* Fee Information */}
      <Card title="Fee Information" className={styles.card}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Static Fee:</Text>
              <Text>
                {orderData.staticFee
                  ? formatDecimalWithCurrency(
                      orderData.staticFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Floating Fee:</Text>
              <Text>
                {orderData.floatingFee
                  ? formatDecimalWithCurrency(
                      orderData.floatingFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Commission Fee:</Text>
              <Text>
                {orderData.commissionFee
                  ? formatDecimalWithCurrency(
                      orderData.commissionFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Recurring Fee:</Text>
              <Text>
                {orderData.recurringFee
                  ? formatDecimalWithCurrency(
                      orderData.recurringFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Entry Fee:</Text>
              <Text>
                {orderData.entryFee
                  ? formatDecimalWithCurrency(
                      orderData.entryFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Exit Fee:</Text>
              <Text>
                {orderData.exitFee
                  ? formatDecimalWithCurrency(
                      orderData.exitFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Fee Charged:</Text>
              <Text>
                {orderData.feeCharged
                  ? formatDecimalWithCurrency(
                      orderData.feeCharged,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className={styles.field}>
              <Text strong>Initial Fee Withheld:</Text>
              <Text>
                {orderData.totalFee
                  ? formatDecimalWithCurrency(
                      orderData.totalFee,
                      orderData.assetCurrency,
                    )
                  : 'N/A'}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      <DerivedMetrics data={orderData} />

      {/* Fee Schedule Table */}
      {orderData.feeSchedule && orderData.feeSchedule.length > 0 && (
        <Card title="Fee Schedule" className={styles.card}>
          <Table
            dataSource={orderData.feeSchedule}
            columns={feeScheduleColumns}
            rowKey="feeName"
            pagination={false}
            scroll={{x: true}}
            size="small"
          />
        </Card>
      )}

      {/* History Table */}
      {orderData.history && orderData.history.length > 0 && (
        <Card title="Order History" className={styles.card}>
          <Table
            dataSource={orderData.history}
            columns={historyColumns}
            rowKey="createdAt"
            pagination={false}
            scroll={{x: true}}
            size="small"
          />
        </Card>
      )}

      {/* Notes Table */}
      {orderData.notes && orderData.notes.length > 0 && (
        <Card title="Order Notes" className={styles.card}>
          <Table
            dataSource={orderData.notes}
            columns={notesColumns}
            rowKey="id"
            pagination={false}
            scroll={{x: true}}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};
